import React, { useRef, useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import * as dagre from 'dagre';
import {
  DependencyGraph,
  LineageNode,
  LineageEdge,
  LineageSelectionState
} from '../../../types/lineage.types';
import { findUpstreamDependencies, findDownstreamDependents } from '../../utils/lineageGraphBuilder';
import * as styles from './LineageGraph.css.ts';

interface LineageGraphProps {
  graph: DependencyGraph;
  selectionState: LineageSelectionState;
  onNodeSelect: (nodeId: string | null) => void;
  onNodeDoubleClick?: (nodeId: string) => void;
  onNodeContextMenu?: (nodeId: string, x: number, y: number) => void;
  onSelectionStateChange: (state: LineageSelectionState) => void;
}

const LineageGraph: React.FC<LineageGraphProps> = ({
  graph,
  selectionState,
  onNodeSelect,
  onNodeDoubleClick,
  onNodeContextMenu,
  onSelectionStateChange
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  // コールバック関数をrefに保存して最新の値を参照できるようにする
  const onSelectionStateChangeRef = useRef(onSelectionStateChange);
  useEffect(() => {
    onSelectionStateChangeRef.current = onSelectionStateChange;
  }, [onSelectionStateChange]);

  const onNodeSelectRef = useRef(onNodeSelect);
  useEffect(() => {
    onNodeSelectRef.current = onNodeSelect;
  }, [onNodeSelect]);

  const onNodeDoubleClickRef = useRef(onNodeDoubleClick);
  useEffect(() => {
    onNodeDoubleClickRef.current = onNodeDoubleClick;
  }, [onNodeDoubleClick]);

  const onNodeContextMenuRef = useRef(onNodeContextMenu);
  useEffect(() => {
    onNodeContextMenuRef.current = onNodeContextMenu;
  }, [onNodeContextMenu]);
  
  // D3要素の参照を保持
  const d3Refs = useRef<{
    svg?: d3.Selection<SVGSVGElement, unknown, null, undefined>;
    g?: d3.Selection<SVGGElement, unknown, null, undefined>;
    zoom?: d3.ZoomBehavior<SVGSVGElement, unknown>;
    nodeGroup?: d3.Selection<SVGGElement, unknown, null, undefined>;
    linkGroup?: d3.Selection<SVGGElement, unknown, null, undefined>;
    dagreGraph?: dagre.graphlib.Graph;
  }>({});
  
  // ノードの色を取得
  const getNodeColor = (node: LineageNode): string => {
    switch (node.type) {
      case 'primary-dataset':
        return '#3B82F6'; // blue
      case 'derived-dataset':
        return '#8B5CF6'; // purple
      case 'report':
        return '#10B981'; // green
      case 'tab':
        return '#F59E0B'; // amber
      case 'model':
        return '#EF4444'; // red
      default:
        return '#6B7280'; // gray
    }
  };

  // エッジの色を取得
  const getEdgeColor = (edge: LineageEdge): string => {
    switch (edge.type) {
      case 'derives-from':
        return '#8B5CF6'; // purple - 派生関係
      case 'references':
        return '#3B82F6'; // blue - 参照関係
      case 'displays':
        return '#10B981'; // green - 表示関係
      default:
        return '#999';
    }
  };
  
  // ノードのタイプ名を取得
  const getNodeTypeName = (node: LineageNode): string => {
    switch (node.type) {
      case 'primary-dataset':
        return 'Dataset';
      case 'derived-dataset':
        return 'Dataset (derived)';
      case 'report':
        return 'Report';
      case 'tab':
        return 'Tab';
      case 'model':
        return 'Model';
      default:
        return 'Unknown';
    }
  };
  
  // 初期化（一度だけ実行）
  useEffect(() => {
    if (!svgRef.current) return;
    
    const svg = d3.select(svgRef.current);
    d3Refs.current.svg = svg;
    
    // メインのグループ
    const g = svg.append('g');
    d3Refs.current.g = g;
    
    // ズーム機能
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });
    
    svg.call(zoom);
    d3Refs.current.zoom = zoom;
    
    // 矢印マーカーと影の定義
    const defs = svg.append('defs');

    // 影のフィルタ
    const filter = defs.append('filter')
      .attr('id', 'node-shadow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');

    filter.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', 3);

    filter.append('feOffset')
      .attr('dx', 0)
      .attr('dy', 2)
      .attr('result', 'offsetblur');

    filter.append('feComponentTransfer')
      .append('feFuncA')
      .attr('type', 'linear')
      .attr('slope', 0.2);

    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode');
    feMerge.append('feMergeNode')
      .attr('in', 'SourceGraphic');

    // 矢印マーカー（エッジタイプごとに色を設定）
    const edgeTypeColors: Record<string, string> = {
      'derives-from': '#8B5CF6',
      'references': '#3B82F6',
      'displays': '#10B981'
    };

    ['derives-from', 'references', 'displays'].forEach(type => {
      defs.append('marker')
        .attr('id', `arrow-${type}`)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 25)
        .attr('refY', 0)
        .attr('markerWidth', 8)
        .attr('markerHeight', 8)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', edgeTypeColors[type] || '#999');
    });
    
    // リンクグループ
    const linkGroup = g.append('g').attr('class', 'links');
    d3Refs.current.linkGroup = linkGroup;
    
    // ノードグループ
    const nodeGroup = g.append('g').attr('class', 'nodes');
    d3Refs.current.nodeGroup = nodeGroup;
    
    // Dagre graphを初期化
    const dagreG = new dagre.graphlib.Graph();
    dagreG.setGraph({
      rankdir: 'LR',  // 左から右へ
      nodesep: 100,   // ノード間の間隔（増加）
      ranksep: 200,   // ランク（層）間の間隔（増加）
      marginx: 50,
      marginy: 50
    });
    dagreG.setDefaultEdgeLabel(() => ({}));
    d3Refs.current.dagreGraph = dagreG;
    
    // 初期ズーム
    svg.call(zoom.transform, d3.zoomIdentity);

    // 背景クリックで選択解除
    svg.on('click', () => {
      onNodeSelectRef.current(null);
    });

    return () => {
      // クリーンアップ
      d3Refs.current = {};
    };
  }, []); // 空の依存配列
  
  // グラフデータの更新
  useEffect(() => {
    const { g, nodeGroup, linkGroup, dagreGraph } = d3Refs.current;
    if (!g || !nodeGroup || !linkGroup || !dagreGraph) return;
    
    // dagreグラフをクリア
    dagreGraph.nodes().forEach(n => dagreGraph.removeNode(n));
    
    // ノードを追加
    graph.nodes.forEach(node => {
      dagreGraph.setNode(node.id, {
        label: node.label,
        width: 180,   // ノードの幅（タイプ名表示のため増加）
        height: 80    // ノードの高さ（増加）
      });
    });
    
    // エッジを追加
    graph.edges.forEach(edge => {
      dagreGraph.setEdge(edge.source, edge.target);
    });
    
    // レイアウトを計算
    dagre.layout(dagreGraph);
    
    // 計算された位置をノードに適用
    interface NodeWithPosition extends LineageNode {
      x?: number;
      y?: number;
    }
    
    graph.nodes.forEach(node => {
      const dagreNode = dagreGraph.node(node.id);
      if (dagreNode) {
        (node as NodeWithPosition).x = dagreNode.x;
        (node as NodeWithPosition).y = dagreNode.y;
      }
    });
    
    // リンクの更新（Enter-Update-Exitパターン）
    const links = linkGroup.selectAll<SVGPathElement, LineageEdge>('.link')
      .data(graph.edges, d => d.id);
    
    // Exit
    links.exit()
      .transition()
      .duration(300)
      .style('opacity', 0)
      .remove();
    
    // Enter
    const linksEnter = links.enter()
      .append('path')
      .attr('class', 'link')
      .attr('fill', 'none')
      .attr('stroke', d => getEdgeColor(d))
      .attr('stroke-opacity', 0)
      .attr('stroke-width', 2);
    
    // Update
    const linksUpdate = links.merge(linksEnter);
    linksUpdate
      .attr('marker-end', d => `url(#arrow-${d.type})`)
      .transition()
      .duration(300)
      .attr('stroke-opacity', 0.6);
    
    // ノードの更新（Enter-Update-Exitパターン）
    const nodes = nodeGroup.selectAll<SVGGElement, LineageNode>('.node')
      .data(graph.nodes, d => d.id);
    
    // Exit
    nodes.exit()
      .transition()
      .duration(300)
      .style('opacity', 0)
      .remove();
    
    // Enter
    const nodesEnter = nodes.enter()
      .append('g')
      .attr('class', 'node')
      .attr('data-testid', 'lineage-node')
      .attr('data-node-id', d => d.id)
      .style('cursor', 'pointer')
      .style('opacity', 0);
    
    // ノードの楕円
    nodesEnter.append('ellipse')
      .attr('rx', 60)  // 横幅
      .attr('ry', 30)  // 縦幅
      .attr('fill', d => getNodeColor(d))
      .attr('stroke', '#fff')
      .attr('stroke-width', 3)
      .attr('filter', 'url(#node-shadow)');

    // ノードのタイプ名
    nodesEnter.append('text')
      .attr('class', 'node-type')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .style('font-size', '12px')
      .style('font-weight', '600')
      .style('fill', '#fff')
      .style('user-select', 'none')
      .text(d => getNodeTypeName(d));

    // ノードのラベル（データセット名）
    nodesEnter.append('text')
      .attr('class', 'node-label')
      .attr('dy', 45)
      .attr('text-anchor', 'middle')
      .style('font-size', '13px')
      .style('font-weight', '500')
      .style('user-select', 'none')
      .text(d => d.label);
    
    // Update
    const nodesUpdate = nodes.merge(nodesEnter);
    nodesUpdate
      .transition()
      .duration(300)
      .style('opacity', 1);
    
    // クリックイベント
    nodesUpdate.on('click', (event, d) => {
      event.stopPropagation();
      onNodeSelectRef.current(d.id);
    });

    // ダブルクリックイベント
    nodesUpdate.on('dblclick', (event, d) => {
      event.stopPropagation();
      if (onNodeDoubleClickRef.current) {
        onNodeDoubleClickRef.current(d.id);
      }
    });

    // コンテキストメニューイベント
    nodesUpdate.on('contextmenu', (event, d) => {
      event.preventDefault();
      event.stopPropagation();
      if (onNodeContextMenuRef.current) {
        onNodeContextMenuRef.current(d.id, event.clientX, event.clientY);
      }
    });
    
    // 初回描画
    updatePositions();
    
    // 位置更新関数
    function updatePositions() {
      // パスを曲線で描画
      linksUpdate.attr('d', d => {
        const source = graph.nodes.find(n => n.id === d.source) as NodeWithPosition;
        const target = graph.nodes.find(n => n.id === d.target) as NodeWithPosition;
        if (!source || !target) return '';
        
        const sx = source.x || 0;
        const sy = source.y || 0;
        const tx = target.x || 0;
        const ty = target.y || 0;
        
        // 滑らかなベジェ曲線で描画
        const dx = tx - sx;
        const dy = ty - sy;
        
        // コントロールポイントを調整
        const cx = sx + dx * 0.7;
        const cy = sy + dy * 0.3;
        
        return `M${sx},${sy} Q${cx},${cy} ${tx},${ty}`;
      });
      
      nodesUpdate.attr('transform', d => `translate(${(d as NodeWithPosition).x || 0},${(d as NodeWithPosition).y || 0})`);
    }
    
    
  }, [graph, onNodeSelect]);
  
  // 選択状態の更新
  useEffect(() => {
    const { nodeGroup, linkGroup } = d3Refs.current;
    if (!nodeGroup || !linkGroup) return;
    
    // ノードのハイライト
    nodeGroup.selectAll<SVGGElement, LineageNode>('.node')
      .style('opacity', d => {
        if (selectionState.selectedNodeId === null) return 1;
        if (selectionState.highlightedNodeIds.has(d.id)) return 1;
        return 0.3;
      })
      .select('ellipse')
      .attr('stroke-width', d => d.id === selectionState.selectedNodeId ? 4 : 3);
    
    // エッジのハイライト
    linkGroup.selectAll<SVGLineElement, LineageEdge>('.link')
      .style('opacity', d => {
        if (selectionState.selectedNodeId === null) return 0.6;
        if (selectionState.highlightedEdgeIds.has(d.id)) return 0.8;
        return 0.1;
      });
    
  }, [selectionState]);
  
  // 選択ノードの依存関係を計算（状態更新はしない）
  const { highlightedNodeIds, highlightedEdgeIds } = useMemo(() => {
    if (!selectionState.selectedNodeId) {
      return {
        highlightedNodeIds: new Set<string>(),
        highlightedEdgeIds: new Set<string>()
      };
    }
    
    // 上流と下流の依存関係を取得
    const upstream = findUpstreamDependencies(graph, selectionState.selectedNodeId);
    const downstream = findDownstreamDependents(graph, selectionState.selectedNodeId);
    
    const nodeIds = new Set([...upstream, ...downstream]);
    
    // 関連するエッジをハイライト
    const edgeIds = new Set<string>();
    graph.edges.forEach(edge => {
      if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
        edgeIds.add(edge.id);
      }
    });
    
    return {
      highlightedNodeIds: nodeIds,
      highlightedEdgeIds: edgeIds
    };
  }, [selectionState.selectedNodeId, graph]);
  
  // ハイライト状態が変わったときに親に通知
  useEffect(() => {
    // 実際にハイライト状態が変更された場合のみ通知
    if (
      selectionState.highlightedNodeIds.size !== highlightedNodeIds.size ||
      selectionState.highlightedEdgeIds.size !== highlightedEdgeIds.size ||
      [...selectionState.highlightedNodeIds].some(id => !highlightedNodeIds.has(id)) ||
      [...selectionState.highlightedEdgeIds].some(id => !highlightedEdgeIds.has(id))
    ) {
      onSelectionStateChangeRef.current({
        ...selectionState,
        highlightedNodeIds,
        highlightedEdgeIds
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedNodeIds, highlightedEdgeIds]); // selectionStateを依存配列から除外
  
  return (
    <svg
      ref={svgRef}
      className={styles.svgCanvas}
    />
  );
};

export default LineageGraph;